"use client";

import { useLayoutEffect } from "react";
import { I18nextProvider } from "react-i18next";

import i18n, { initI18n } from "@/i18n";
import {
  type AppLanguage,
  normalizeLanguage,
  persistLanguage,
  readClientLanguage,
} from "@/i18n/settings";

type I18nProviderProps = {
  children: React.ReactNode;
  initialLocale: AppLanguage;
};

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const locale = readClientLanguage() ?? initialLocale;
  initI18n(locale);

  useLayoutEffect(() => {
    const target = readClientLanguage() ?? initialLocale;
    const current = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

    if (current !== target) {
      void i18n.changeLanguage(target);
    }

    persistLanguage(target);
    document.documentElement.style.visibility = "";
  }, [initialLocale]);

  useLayoutEffect(() => {
    const onLanguageChanged = (nextLanguage: string) => {
      persistLanguage(normalizeLanguage(nextLanguage));
    };

    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
